import { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  Select,
  Group,
  Grid,
  Title,
  Text,
  NumberInput,
  Image,
  Switch,
  ScrollArea
} from '@mantine/core';
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { ExportableMantineReactTable, useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { PageFilterBanner } from '../components/PageFilterBanner';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import { mergeDefinedFields, formKeysPresentInRow } from '../utility/excelImportHelpers';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import { useDropzone } from 'react-dropzone';
import { MRT_DensityState } from 'mantine-react-table';
import { readApiErrorMessage } from '../utility/apiErrors';
import Environment from '../utility/environment';
import { useMrtTableContainerProps, useModalScrollAreaHeight, usePageTableShellStyle } from '../utility/mobilePageLayout';
import { useAuth } from '../contexts/AuthProvider';
import { readPageFilterParam } from '../utility/pageNavigationFilters';
// import { SortableContainer, SortableElement } from 'react-sortable';
import { IconTrash } from '@tabler/icons-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSSProperties } from 'react';


type Product = {
  id: number;
  uuid: string;
  name: string;
  regulatory_class: string;
  manufacturer: string;
  model_number: string;
  barcode: string;
  photos: (string | File)[];
  description: string;
  long_description: string;
  price: number;
  currency: string;
  iva: number;
  status: boolean;
  visual_validation: boolean;
  functionality_verification: boolean;
  accessories_check: boolean;
  update_at: string;
};

type ProductForm = Omit<Product, 'id' | 'uuid' | 'update_at'>;

const initialForm: ProductForm = {
  name: '',
  regulatory_class: '',
  manufacturer: '',
  model_number: '',
  barcode: '',
  photos: [],
  description: '',
  long_description: '',
  currency: 'USD',
  price: 0,
  iva: 0,
  status: true,
  visual_validation: false,
  functionality_verification: false,
  accessories_check: false,
};

// Componente para cada imagem
type SortableImageProps = {
  photo: string | File;
  index: number;
  onRemove: (index: number) => void;
};

const SortableImage = ({ photo, index, onRemove }: SortableImageProps) => {
  const { t } = useTranslation();
  // Usa o hook useSortable para o drag
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: `photo-${index}`,
  });

  // Estilo de transformação para o drag
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: '10px',
    position: 'relative', // Garante que a imagem e o botão não se sobreponham
    zIndex: transform ? 0 : 1, // Durante o drag, z-index é menor para não cobrir o botão
  };

  return (
    <div style={{ position: 'relative', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
      {/* Área de arrasto da imagem */}
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        <Image
          src={typeof photo === 'string' ? Environment.VITE_API_URL + photo : URL.createObjectURL(photo)}
          alt={t('products.imageAlt', { index: index + 1 })}
          width="auto"
          height="20vh"
          radius="md"
          withPlaceholder
        />
      </div>

      {/* Botão de excluir independente da área de drag */}
      {!transform && (
        <Button
          size="xs"
          color="red"
          style={{
            position: 'absolute',
            top: 5,
            right: 5,
            zIndex: 2, // Garantir que o botão fique sempre na frente da imagem
          }}
          sx={{ backgroundColor: 'rgba(48,113,91,0.5)' }}
          onClick={() => onRemove(index)} // Clica para remover a imagem
        >
          <IconTrash size={16} />
        </Button>
      )}

    </div>
  );
};
type SortableImageListProps = {
  photos: (string | File)[];
  onRemove: (index: number) => void;
  onSortEnd: (oldIndex: number, newIndex: number) => void;
};

const SortableImageList = ({ photos, onRemove, onSortEnd }: SortableImageListProps) => {
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = photos.findIndex((_, index) => `photo-${index}` === active.id);
      const newIndex = photos.findIndex((_, index) => `photo-${index}` === over?.id);
      onSortEnd(oldIndex, newIndex);
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <SortableContext items={photos.map((_, index) => `photo-${index}`)} strategy={verticalListSortingStrategy}>
        <Group spacing="xs" style={{ flexWrap: 'wrap' }}>
          {photos.map((photo, index) => (
            <SortableImage key={`photo-${index}`} index={index} photo={photo} onRemove={onRemove} />
          ))}
        </Group>
      </SortableContext>
    </DndContext>
  );
};

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const { accessToken, actualEnterprise } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const productIdFilter = readPageFilterParam(location.search, 'productId');
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const modalScrollAreaHeight = useModalScrollAreaHeight();

  const mrtLocalization = useMemo(
    () => buildMrtLocalization(t),
    [t, i18n.language],
  );

  const [products, setProducts] = useState<Product[]>([]);

  const [modalOpened, setModalOpened] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [productForm, setProductForm] = useState<ProductForm>(initialForm);
  const [errorMessage, setErrorMessage] = useState('');

  const [removedImages, setRemovedImages] = useState<string[]>([]);

  // Estado para armazenar o arquivo importado via dropzone
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);
  const [notification, setNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);

  // CONSTANTES
  const descriptionLength = 2000;
  const productName = (product: ProductForm) => [product.manufacturer, product.description, product.model_number].map(value => (value || '').trim()).filter(Boolean).join(' - ');
  const regulatoryClassLength = 50;
  const manufactoryLength = 50;
  const modelNumberLength = 50;


  useEffect(() => {
    getProducts();
  }, [accessToken, actualEnterprise]);

  useEffect(() => {
    document.title = t('products.documentTitle');
  }, [t, i18n.language]);

  const isFormValid = () => {
    return (
      productName(productForm).length <= 2206 &&
      productForm.long_description.length <= 8000 &&
      productForm.currency.trim() !== '' &&
      productForm.price > 0 &&
      productForm.description.trim() !== '' &&
      productForm.description.length <= 2000 &&
      productForm.status !== null
    );
  };

  const openModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({ ...product });
    } else {
      setEditingProduct(null);
      setProductForm(initialForm);
    }
    setIsEditingProduct(false);
    setModalOpened(true);
    setAutoImportFile(null);
    setErrorMessage('');
    setNotification(null);
    setImportErrors([]);
    setRemovedImages([]);
  };


  const getProducts = () => {
    if (!actualEnterprise?.id) {
      return;
    }
    fetch(`${Environment.VITE_API_URL}/api/v1/products/${actualEnterprise.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) {
          console.error('Failed to load products:', res.status);
          setProducts([]);
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) {
          setProducts([]);
          return;
        }
        const formattedProducts: Product[] = data.map((product: any, index: number) => ({
          id: index + 1,
          uuid: product.id,
          name: product.name,
          manufacturer: product.manufacturer || '',
          model_number: product.model_number || '',
          barcode: product.barcode || '',
          regulatory_class: product.regulatory_class || '',
          currency: product.currency || '',
          price: parseFloat(product.price),
          iva: product.iva != null ? parseFloat(product.iva) : 0,
          status: product.status,
          visual_validation: Boolean(product.visual_validation),
          functionality_verification: Boolean(product.functionality_verification),
          accessories_check: Boolean(product.accessories_check),
          description: product.description,
          long_description: product.long_description || '',
          photos: product.photos.map((photo: any) => photo.url),
          update_at: product.update_at ?? '',
        }));

        setProducts(formattedProducts);
      })
      .catch((err) => console.error('Erro ao buscar Produtos:', err));
  };

  const deleteProduct = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/products/${editingProduct?.uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
          setNotification({ message: t('products.deleteLinkedError'), color: 'red' });
          setErrorMessage(t('products.deleteLinkedError'));
        }

        if (response.status == 200) {
          setModalOpened(false);
          getProducts();
        }
      })
      .catch(() => {
        // console.error(error);
        setModalOpened(true);
      });
  };

  const updateProduct = async () => {
    if (!editingProduct?.uuid) {
      setErrorMessage(t('products.noProductSelected'));
      return;
    }
    removeImages();
    await sendImages();
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/products/${editingProduct.uuid}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify(formatProduct(productForm)),
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const detail = typeof body.detail === 'string' ? body.detail : t('products.updateFailed');
        setErrorMessage(detail);
        return;
      }
      setModalOpened(false);
    } catch (error) {
      console.error(error);
      setErrorMessage(t('products.updateFailed'));
    } finally {
      getProducts();
    }
  };
  const removeImages = () => {
    if (removedImages.length === 0) {
      return;
    }
    fetch(`${Environment.VITE_API_URL}/api/v1/delete-image`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(removedImages),
    }).catch((error) => {
      console.error(error);
    });
  };

  const sendImages = async () => {
    for (let index = 0; index < productForm.photos.length; index++) {
      const file = productForm.photos[index];
      if (file instanceof File) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const response = await fetch(`${Environment.VITE_API_URL}/api/v1/upload-image`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
            body: formData,
          })

          if (response.ok) {
            const data = await response.json();
            const photos = productForm.photos
            photos[index] = data.image_url
            setProductForm({
              ...productForm,
              photos: photos,
            });
          }
        } catch (error) {
        }
      }
    }
  }


  const createProduct = async () => {
    await sendImages();

    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/products/${actualEnterprise?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(formatProduct(productForm)),
      });

      if (!response.ok) {
        const message = await readApiErrorMessage(response, t('common.saveFailed'));
        setNotification({ message, color: 'red' });
        return;
      }

      setModalOpened(false);
      getProducts();
    } catch (error) {
      console.error(error);
      setNotification({ message: t('common.saveFailed'), color: 'red' });
    }
  };

  const formatProduct = (product: ProductForm) => {
    return {
      name: productName(product),
      regulatory_class: product.regulatory_class,
      manufacturer: product.manufacturer,
      model_number: product.model_number,
      barcode: product.barcode || null,
      currency: product.currency,
      price: product.price,
      iva: product.iva ?? 0,
      description: product.description,
      long_description: product.long_description,
      photos: product.photos.map((photo, index) => { return { url: photo, position: index + 1 } }),
      status: product.status,
      visual_validation: product.visual_validation,
      functionality_verification: product.functionality_verification,
      accessories_check: product.accessories_check,
    };
  };



  const parseProductStatus = (value: unknown): boolean => {
    if (value === true || value === 'true' || value === 1 || value === '1') {
      return true;
    }
    if (value === false || value === 'false' || value === 0 || value === '0') {
      return false;
    }
    return Boolean(value);
  };

  const rowToProductForm = (row: Record<string, unknown>): ProductForm => ({
    name: String(row['name'] ?? row['Nome'] ?? row['Name'] ?? row[t('products.product')] ?? ''),
    regulatory_class: String(row['regulatory_class'] ?? row['Regulatory Class'] ?? row[t('products.regulatoryClass')] ?? ''),
    manufacturer: String(row['manufacturer'] ?? row['Manufactory'] ?? row['Manufacturer'] ?? row[t('products.manufacturer')] ?? ''),
    model_number: String(row['model_number'] ?? row['Model Number'] ?? row[t('products.modelNumber')] ?? ''),
    barcode: String(row['barcode'] ?? row['Barcode'] ?? row[t('products.barcode')] ?? ''),
    currency: String(row['currency'] ?? row['Currency'] ?? row[t('products.currency')] ?? ''),
    price: Number(row['price'] ?? row['Preço'] ?? row['Price'] ?? row[t('products.price')] ?? 0),
    iva: (row['iva'] ?? row['IVA']) != null ? Number(row['iva'] ?? row['IVA']) || 0 : Number(row[t('products.iva')] ?? 0) || 0,
    description: String(row['description'] ?? row['Descrição'] ?? row['Description'] ?? row[t('products.description')] ?? ''),
    long_description: String(row['long_description'] ?? row['Long Description'] ?? row[t('products.longDescription')] ?? ''),
    photos: [],
    status: parseProductStatus(row['status'] ?? row['Status'] ?? row[t('common.status')]),
    visual_validation: false,
    functionality_verification: false,
    accessories_check: false,
  });

  const validateProductForm = (product: ProductForm, line: number): string[] => {
    const rowErrors: string[] = [];
    if (product.description.length > 2000 || product.long_description.length > 8000) rowErrors.push(t('products.descriptionTooLong'));
    if (!product.currency) rowErrors.push(t('products.validation.currencyRequired', { defaultValue: 'Currency is required' }));
    if (product.currency.length !== 3) rowErrors.push(t('products.validation.currencyLength', { defaultValue: 'Currency must be 3 characters' }));
    if (Number.isNaN(Number(product.price))) rowErrors.push(t('products.validation.priceNumber', { defaultValue: 'Price must be a valid number' }));
    if (!product.price) rowErrors.push(t('products.validation.priceRequired', { defaultValue: 'Price is required' }));
    if (!product.description) rowErrors.push(t('products.validation.descriptionRequired', { defaultValue: 'Description is required' }));
    if (product.status !== true && product.status !== false) {
      rowErrors.push(t('products.validation.statusRequired', { defaultValue: 'Status is required' }));
    }
    if (rowErrors.length > 0) {
      return [t('common.import.rowError', { line, errors: rowErrors.join(' ') })];
    }
    return [];
  };

  const executeProductImport = async (
    createForms: ProductForm[],
    updateEntries: Array<{ id: string; form: ProductForm }>,
    deleteIds: string[],
  ) => {
    const ok = await executeImportUpsert({
      createPayloads: createForms.map(formatProduct),
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: formatProduct(entry.form),
      })),
      deleteIds,
      bulkCreateUrl: `${Environment.VITE_API_URL}/api/v1/products_bulk`,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/products/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/products/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setNotification({ message: t('common.import.success'), color: 'green' });
    setModalOpened(false);
    setImportModalOpened(false);
    getProducts();
  };

  const handleAutoImport = async () => {
    if (!autoImportFile) {
      return;
    }
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(products.map((product) => product.uuid));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const createForms: ProductForm[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const product = rowToProductForm(row);
        errors.push(...validateProductForm(product, line));
        createForms.push(product);
      }

      const updateEntries: Array<{ id: string; form: ProductForm }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = products.find((product) => product.uuid === entry.id);
        if (!existing) {
          continue;
        }
        const patch = rowToProductForm(entry.row);
        const merged = mergeDefinedFields(
          {
            name: existing.name,
            regulatory_class: existing.regulatory_class,
            manufacturer: existing.manufacturer,
            model_number: existing.model_number,
            barcode: existing.barcode,
            currency: existing.currency,
            price: existing.price,
            iva: existing.iva,
            description: existing.description,
            long_description: existing.long_description,
            photos: existing.photos,
            status: existing.status,
            visual_validation: existing.visual_validation,
            functionality_verification: existing.functionality_verification,
            accessories_check: existing.accessories_check,
          },
          patch,
          formKeysPresentInRow(entry.row, {
            name:'name', Nome:'name', Name:'name', [t('products.product')]:'name',
            description:'description', Descrição:'description', Description:'description', [t('products.description')]:'description',
            long_description:'long_description', 'Long Description':'long_description', [t('products.longDescription')]:'long_description',
            manufacturer:'manufacturer', Manufactory:'manufacturer', Manufacturer:'manufacturer', [t('products.manufacturer')]:'manufacturer',
            model_number:'model_number', 'Model Number':'model_number', [t('products.modelNumber')]:'model_number',
            regulatory_class:'regulatory_class', 'Regulatory Class':'regulatory_class', [t('products.regulatoryClass')]:'regulatory_class',
            barcode:'barcode', Barcode:'barcode', [t('products.barcode')]:'barcode',
            currency:'currency', Currency:'currency', [t('products.currency')]:'currency',
            price:'price', Preço:'price', Price:'price', [t('products.price')]:'price',
            iva:'iva', IVA:'iva', [t('products.iva')]:'iva',
            status:'status', Status:'status', [t('common.status')]:'status',
          }),
        );
        errors.push(...validateProductForm(merged, line));
        updateEntries.push({ id: entry.id, form: merged });
      }

      if (errors.length > 0) {
        setNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createForms.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeProductImport(createForms, updateEntries, classified.deleteIds),
      });
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      setNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const { getRootProps: getImageRootProps, getInputProps: getImageInputProps, isDragActive: isImageDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setProductForm({
        ...productForm,
        photos: productForm.photos.concat(acceptedFiles),
      })
    },
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    }, // Aceitar todos os tipos de imagem
    maxSize: 5 * 1024 ** 2, // 5MB máximo por imagem
    multiple: true,
  });

  // Função para reordenar as imagens
  const handleSortEnd = (oldIndex: number, newIndex: number) => {
    setProductForm((prev) => {
      const updatedPhotos = [...prev.photos];
      const [movedItem] = updatedPhotos.splice(oldIndex, 1);
      updatedPhotos.splice(newIndex, 0, movedItem);
      return {
        ...prev,
        photos: updatedPhotos,
      };
    });
  };

  const removeImage = (index: number) => {
    console.log('Removendo imagem na posição:', index);
    if (typeof productForm.photos[index] === "string") {
      const imageUrl = productForm.photos[index] as string;
      setRemovedImages(() => [...removedImages, imageUrl]);
    }

    setProductForm((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const columns = useMemo<MRT_ColumnDef<Product>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'photos',
        header: t('products.image'),
        Cell: ({ cell }) => {
          const photos = cell.getValue<any[]>();
          const imageUrl = photos.length ? photos[0] : '';
          return imageUrl ? (
            <div style={{ position: 'relative', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
              <Image
                src={Environment.VITE_API_URL + imageUrl}
                alt={t('products.productAlt')}
                width="auto"
                height="10vh"
                radius="sm"
              />
            </div>
          ) : (
            null
          );
        },
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'name',
        header: t('products.product'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'manufacturer',
        header: t('products.manufacturer'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'model_number',
        header: t('products.modelNumber'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'description',
        header: t('products.description'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'long_description',
        header: t('products.longDescription'),
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'currency',
        header: t('products.currency'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'price',
        header: t('products.price'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) =>
          Number(cell.getValue<number>() ?? 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
      {
        accessorKey: 'iva',
        header: t('products.iva'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => `${Number(cell.getValue<number>() ?? 0).toFixed(2)}%`,
      },
      {
        accessorKey: 'status',
        header: t('common.status'),
        Cell: ({ cell }) => (cell.getValue<boolean>() ? t('common.active') : t('common.inactive')),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
    ],
      ['name', 'manufacturer', 'model_number', 'description', 'long_description'],
    ),
    [t, i18n.language]
  );

  const handleRowDoubleClick = (rowData: Product) => {
    setModalOpened(true);
    setEditingProduct(rowData);
    setProductForm(rowData);
    setIsEditingProduct(true);
    setErrorMessage('');
    setNotification(null);
    setImportErrors([]);
    setRemovedImages([]);
    console.log('Dados da linha clicada:', rowData);
  };

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setNotification(null);
  };

  const filteredProducts = useMemo(
    () =>
      productIdFilter ? products.filter((product) => product.uuid === productIdFilter) : products,
    [products, productIdFilter],
  );

  const filteredProductName = useMemo(() => {
    if (!productIdFilter) return '';
    return filteredProducts[0]?.name ?? productIdFilter;
  }, [productIdFilter, filteredProducts]);

  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: filteredProducts,
      localization: mrtLocalization,
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      initialState: {
        density: 'sm' as MRT_DensityState,
        showGlobalFilter: true,
        pagination: {
          pageSize: 20,
          pageIndex: 0,
        },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: {
        sx: {
          display: 'table',
          tableLayout: 'auto',
          borderCollapse: 'separate',
          '& .mantine-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1px 1px',
          },
          '& .mantine-TextInput-input': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000',
          },
          '& th, & td': {
            borderRadius: '4px',
          },
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'rgba(48, 113, 91, 0.9)',
            color: '#fff',
          },
        },
      },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Product> }) => ({
        onDoubleClick: () => handleRowDoubleClick(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'uuid',
      filenamePrefix: 'products',
      sheetName: 'Products',
      getDeletedValue: (row) => !row.status,
      getCellValue: (row, columnId) => {
        if (columnId === 'photos') {
          return row.photos.length ? String(row.photos[0]) : '';
        }
        if (columnId === 'price') {
          return Number(row.price ?? 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        if (columnId === 'iva') {
          return `${Number(row.iva ?? 0).toFixed(2)}%`;
        }
        if (columnId === 'status') {
          return row.status ? t('common.active') : t('common.inactive');
        }
        return row[columnId as keyof Product];
      },
      onOpenImport: openImportModal,
    },
  );

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={notification}
    />
  );

  return (
    <>
      {/* Cabeçalho com botão */}
      <Group position="apart" align="center" mb="md">
        <Title order={1}>{t('products.title')}</Title>
        <Group>
          <Button onClick={() => openModal()}>{t('products.addProduct')}</Button>
        </Group>
      </Group>

      {productIdFilter && (
        <PageFilterBanner
          message={t('products.filteredByProduct', { name: filteredProductName })}
          onClear={() => navigate('/products')}
        />
      )}

      <div style={tableShellStyle}>
        <ExportableMantineReactTable table={table} exportModal={exportModal} importModal={importModal} />
      </div>

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="products"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      {/* Modal para adicionar/editar ou importar clientes */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        title={
          editingProduct
            ? t('products.updateProduct')
            : t('products.addNewProduct')
        }
        size="xl"
        styles={{ body: { padding: '0' } }}
      >
        <ScrollArea w="100%" h={modalScrollAreaHeight}>
              <div style={{ padding: '1rem' }}>
                {/* -- Campos Obrigatórios -- */}
                <TextInput
                  label={t('products.product')}
                  description={t('products.automaticName')}
                  value={productName(productForm)}
                  readOnly mb="sm"
                  styles={{ input: { backgroundColor:'#f1f3f5', color:'#868e96', borderColor:'#dee2e6', cursor:'default' } }}
                />
                <Grid gutter="sm" mb="sm" align="flex-start">
                  <Grid.Col span={12} sm={3}>
                    <TextInput
                      label={t('products.manufacturer')}
                      value={productForm.manufacturer}
                      onChange={event => setProductForm({...productForm, manufacturer:event.currentTarget.value})}
                      maxLength={manufactoryLength}
                    />
                  </Grid.Col>
                  <Grid.Col span={12} sm={6}>
                    <TextInput
                      label={t('products.description')}
                      value={productForm.description}
                      onChange={event => setProductForm({...productForm, description:event.currentTarget.value})}
                      maxLength={descriptionLength} required
                    />
                  </Grid.Col>
                  <Grid.Col span={12} sm={3}>
                    <TextInput
                      label={t('products.modelNumber')}
                      value={productForm.model_number}
                      onChange={event => setProductForm({...productForm, model_number:event.currentTarget.value})}
                      maxLength={modelNumberLength}
                    />
                  </Grid.Col>
                </Grid>
                <Group grow mb="sm">
                  <Textarea
                    label={t('products.longDescription')}
                    placeholder={t('products.longDescriptionHint')}
                    value={productForm.long_description}
                    onChange={(e) =>
                      setProductForm({ ...productForm, long_description: e.target.value })
                    }
                    mb="sm"
                    autosize minRows={3} maxRows={8}
                    description={t('products.longDescriptionHint')}
                    maxLength={8000}
                  />
                </Group>
                <Text className={`text-input-counter ${productForm.long_description.length > 8000 ? "error" : ""}`}>
                  {t('common.characters', { count: productForm.long_description.length, max: 8000 })}
                </Text>

                <Group grow mb="sm">
                  <TextInput
                    label={t('products.barcode')}
                    placeholder={t('products.barcodePlaceholder')}
                    value={productForm.barcode}
                    onChange={(e) =>
                      setProductForm({ ...productForm, barcode: e.target.value })
                    }
                    mb="sm"
                    maxLength={50}
                  />
                </Group>

                <Group grow mb="sm">
                  <TextInput
                    label={t('products.regulatoryClass')}
                    placeholder={t('products.regulatoryClassPlaceholder')}
                    value={productForm.regulatory_class}
                    onChange={(e) =>
                      setProductForm({ ...productForm, regulatory_class: e.target.value })
                    }
                    mb="sm"
                    maxLength={regulatoryClassLength}
                  />
                </Group>
                <Text className={`text-input-counter ${(productForm.regulatory_class?.length ?? 0) > regulatoryClassLength ? "error" : ""}`}>
                  {t('common.characters', { count: productForm.regulatory_class?.length ?? 0, max: regulatoryClassLength })}
                </Text>

                <Text>
                  {t('products.images')}
                </Text>
                {/* Dragzone para Imagens */}
                <div>
                  {/* Área de dropzone */}
                  <div
                    {...getImageRootProps()}
                    style={{
                      border: '2px dashed #ddd',
                      padding: '20px',
                      textAlign: 'center',
                      marginBottom: '20px',
                    }}
                  >
                    <input {...getImageInputProps()} />
                    {isImageDragActive ? (
                      <Text color="blue">{t('products.dropImagesHere')}</Text>
                    ) : (
                      <Text>{t('products.dragDropImages')}</Text>
                    )}
                  </div>

                  {/* Lista ordenável de imagens */}
                  {productForm.photos.length > 0 && (
                    <SortableImageList
                      photos={productForm.photos}
                      onSortEnd={handleSortEnd}
                      onRemove={removeImage}
                    />
                  )}
                </div>



                <Group grow mb="sm">
                  <Select
                    label={t('products.currency')}
                    placeholder={t('common.select')}
                    value={productForm.currency}
                    onChange={(value) =>
                      setProductForm({
                        ...productForm,
                        currency: value as
                          | 'BRL'
                          | 'USD'
                      })
                    }
                    data={[
                      { value: 'BRL', label: 'BRL' },
                      { value: 'USD', label: 'USD' },
                    ]}
                    mb="sm"
                    required
                    disabled={true}
                  />
                  <NumberInput
                    label={t('products.price')}
                    placeholder={t('products.pricePlaceholder')}
                    value={productForm.price}
                    onChange={(value) => setProductForm({ ...productForm, price: value || 0 })}
                    mb="sm"
                    required
                    precision={2}
                  />
                  <NumberInput
                    label={`${t('products.iva')} (%)`}
                    placeholder={t('products.ivaPlaceholder')}
                    value={productForm.iva}
                    onChange={(value) => setProductForm({ ...productForm, iva: typeof value === 'number' ? value : 0 })}
                    min={0}
                    max={100}
                    precision={2}
                    mb="sm"
                  />
                </Group>




                <Switch
                  label={t('common.status')}
                  checked={productForm.status}
                  onChange={(e) =>
                    setProductForm({ ...productForm, status: e.currentTarget.checked })
                  }
                  mb="sm"
                />

                <Text weight={600} size="sm" mb="xs">
                  {t('products.inventoryChecks')}
                </Text>
                <Switch
                  label={t('products.visualValidation')}
                  checked={productForm.visual_validation}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      visual_validation: e.currentTarget.checked,
                    })
                  }
                  mb="sm"
                />
                <Switch
                  label={t('products.functionalityVerification')}
                  checked={productForm.functionality_verification}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      functionality_verification: e.currentTarget.checked,
                    })
                  }
                  mb="sm"
                />
                <Switch
                  label={t('products.accessoriesCheck')}
                  checked={productForm.accessories_check}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      accessories_check: e.currentTarget.checked,
                    })
                  }
                  mb="sm"
                />

                <Group position="center" mt="md" color="red">
                  <Text className="error-message" color="red">
                    {errorMessage !== '' ? errorMessage : null}
                  </Text>
                </Group>
              </div>
            </ScrollArea>
            <Group position="right" mt="md" style={{ margin: '1rem' }}>
              {isEditingProduct && (
                <Button
                  onClick={deleteProduct}
                  sx={{
                    backgroundColor: 'rgba(163, 0, 0, 0.9)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                onClick={isEditingProduct ? updateProduct : createProduct}
                disabled={!isFormValid()}
              >
                {isEditingProduct ? t('common.update') : t('common.save')}
              </Button>
            </Group>
      </Modal>

    </>
  );
}